import type { ItemPatch, PlateItem } from "../api/backlog";
import type { RosterMember } from "../api/roster";
import { memberLabel } from "../members";
import { ChipGroup, type ChipOption, type ChipValue, FilterDisclosure } from "./Chips";

/**
 * The list screen's two filters — **what to show, and whose** (issue #310, `Backlog.dc.html`).
 *
 * ## They narrow what is rendered and nothing else
 *
 * No request gains a parameter and no read is scoped: `Plate.tsx` fetches the same whole list
 * whichever chips are lit, which is `WhoseFilter`'s rule and holds here for the same reason.
 * A filter that narrowed the *read* would be a second projection of the family's record, and
 * the counts beside every heading would then be counts of a filtered read describing itself as
 * the family's list.
 *
 * ## Two groups on one line, because the canvas draws two
 *
 * *Open / Finished / Everything*, a hairline, then *Whose:*. They are separate **groups**
 * rather than one row of six chips, because they answer different questions and a single group
 * would let *Open* and *Dana's* look mutually exclusive.
 *
 * The first group is drawn with no visible label — the three words say what they do — so its
 * name is stated for a screen reader and hidden, which is not the same as inventing a label
 * the canvas does not have (see {@link ChipGroup.hideLegend}).
 *
 * ## Whose, on this screen, means *who is down for it*
 *
 * Not the same question the memory screen asks. A fact has a **subject** (#92: whose life it is
 * about, `family` or a member, stated and never defaulted); an item has an **owner**, which is
 * often nobody — so the chips are the roster plus *Nobody's yet* and there is no *The family's*.
 * The canvas's first chip is *Anyone's* rather than the memory screen's *Everyone's*, which is
 * its own word and is kept: on a list, "everyone's" would read as *jobs everybody has to do*.
 */

/** The lifecycle the list is showing. */
export type Showing = "open" | "finished" | "everything";

export const OPEN: Showing = "open";

const SHOWING_OPTIONS: readonly ChipOption[] = [
  { value: "open", label: "Open" },
  { value: "finished", label: "Finished" },
  { value: "everything", label: "Everything" },
];

/** Whether a chip's value is one of the three — see the `onChange` narrowing below. */
function isShowing(value: string): value is Showing {
  return SHOWING_OPTIONS.some((option) => option.value === value);
}

/**
 * Does this item belong under the lifecycle chip?
 *
 * **`finished` is *done or dropped*, not `done`.** `ITEM.status` has three values and the
 * screen offers two buckets, so a dropped item has to land in one of them — and it is off the
 * list, which is what *Finished* means to a family. Filing it under *Open* would put something
 * nobody is going to do back in front of them; leaving it out of both would make *Everything*
 * larger than *Open* plus *Finished*, on a screen that puts a count beside every heading.
 *
 * A status this build has never heard of counts as neither, so it appears only under
 * *Everything* — visible, rather than silently dropped from a list whose counts are claims.
 */
export function showingMatches(item: PlateItem, showing: Showing): boolean {
  if (showing === "everything") return true;
  if (showing === "open") return item.status === "open";
  return item.status === "done" || item.status === "dropped";
}

export const ANYONE: ChipValue = "all";
const NOBODY: ChipValue = "nobody";

/**
 * Does this item belong under the whose chip?
 *
 * **`Nobody's yet` is asked of the rendered `owner`, not of the stored `ownerId`** (PR #336
 * review, finding 1). Those disagree in exactly one case and it is a real one: an item whose
 * owner has **left** keeps their id on the record (ADR 0019 decision 5) while `getRoster` is
 * active-only, so the server resolves `owner` to `null` and the row renders *nobody's yet*.
 * Testing the id instead made that row vanish when the family clicked the very chip whose
 * label it was wearing — and there is no departed-member chip to reach it through, because
 * the panel has no name to put on one.
 *
 * The rule that generalises: **a filter is asked of what the row shows.** Anything else is a
 * screen disagreeing with itself.
 */
export function whoseItem(item: PlateItem, whose: ChipValue): boolean {
  if (whose === ANYONE) return true;
  if (whose === NOBODY) return item.owner === null;
  return item.ownerId === whose;
}

/**
 * The chips, in the canvas's order: anyone's, each member, then the one that is not a person.
 *
 * **The active roster always gets a chip**, so the row is stable while the list loads and a
 * member with nothing on their plate is visibly a member with nothing on their plate.
 *
 * **Somebody who has left gets no chip of their own**, which is where this is coarser than the
 * memory screen's filter. `getRoster` is active-only (ADR 0019 decision 5 keeps a departed
 * member's attribution on the record but not the member), so an item they owned shows *no
 * owner* rather than a ULID — and it is reachable under *Nobody's yet*, whose label is honest
 * about the state the row is actually in on this screen. That reachability is the whole reason
 * {@link whoseItem} asks about `owner` rather than `ownerId`. The memory screen needs its
 * extra chip because a fact's subject is never *nobody*; an item's owner routinely is.
 */
export function whoseItemOptions(members: readonly RosterMember[]): readonly ChipOption[] {
  return [
    { value: ANYONE, label: "Anyone's" },
    // **`memberLabel`, not `member.name`** (PR #336 review, finding 5). Two active members may
    // share a display name, and two identical *Sam's* buttons backed by different ids is a
    // control a family cannot use — worse than the pickers this rule already covers, because
    // there is no field beside it to disambiguate from.
    ...members.map((member) => ({
      value: member.userId,
      label: `${memberLabel(member, members)}'s`,
    })),
    { value: NOBODY, label: "Nobody's yet", faint: true },
  ];
}

export function PlateFilters({
  showing,
  whose,
  members,
  disabled = false,
  onShowing,
  onWhose,
}: {
  showing: Showing;
  whose: ChipValue;
  members: readonly RosterMember[];
  /** A form or a confirmation is open — see `Plate.tsx` for why the chips wait for it. */
  disabled?: boolean;
  onShowing: (next: Showing) => void;
  onWhose: (next: ChipValue) => void;
}) {
  return (
    <div className="whose">
      <div className="whose__row">
        <ChipGroup
          legend="Show:"
          hideLegend
          value={showing}
          options={SHOWING_OPTIONS}
          disabled={disabled}
          // The chips are the only source of this value, and every one of them is a `Showing`
          // — but the group's callback is typed for a plain chip, so it is narrowed rather
          // than asserted. An unknown value keeps the current one, which is a no-op.
          onChange={(next) => {
            if (isShowing(next)) onShowing(next);
          }}
        />
        <span className="whose__divider" aria-hidden="true" />
        <ChipGroup
          legend="Whose:"
          value={whose}
          options={whoseItemOptions(members)}
          disabled={disabled}
          onChange={onWhose}
        />
      </div>
      <FilterDisclosure frozen={disabled} />
    </div>
  );
}

/**
 * Would this item, **once the patch lands**, still show under the current chip?
 *
 * The `whoseAdmits` question `Memory.tsx` asks about a fact it is about to save (PR #336
 * review, finding 3), and it exists for the same failure: a family filtered to *Dana's* who
 * hands an item to Sam watches the row they just corrected vanish, and since an applied write
 * relays no message on this screen, the only visible outcome of pressing *Edit* is that the
 * item disappeared.
 *
 * **Only an owner change can move a row out of this filter**, which is why nothing here looks
 * at `showing`: `update` cannot touch `status`, and the one act that does — *Done* — is
 * *supposed* to take the row off an *Open* list. That is what the canvas says *Done* means.
 *
 * An unchanged owner falls through to {@link whoseItem} on the row as it stands, which is
 * `true` by construction: the family is looking at it.
 */
export function whoseAdmitsPatch(item: PlateItem, patch: ItemPatch, whose: ChipValue): boolean {
  if (whose === ANYONE) return true;
  if (patch.ownerMemberId !== undefined) return whose === patch.ownerMemberId;
  if (patch.clear?.includes("ownerMemberId") === true) return whose === NOBODY;
  return whoseItem(item, whose);
}
